// @ts-check
"use strict"

import React, { forwardRef, useEffect, useId, useImperativeHandle, useMemo, useRef, useState } from "react"
import { PatientAutoComplete } from "@minohealth/common/components/patients-autocomplete"
import Combobox from "@minohealth/common/components/combobox"
import AutoResizingTextarea from "@minohealth/common/components/AutoResizingTextarea"
import useSavePatient from "@minohealth/common/hooks/useSavePatient"
import mixpanel from "mixpanel-browser"
import { capitalise } from "@minohealth/common/utils/string-formatters"
import { randomUUID } from "@minohealth/common/utils/random"

import { IoMdClose } from 'react-icons/io'

// import "./chat-report-forms.css"
import "./report-form-dialog.css"
import { useChatContext } from "@/services/chat-provider"

/**
 * @typedef {Object} ReportFormDialogComponent
 * @property {boolean} isModalOpen
 * @property {(images: ChatReportInput["images"], options: { onSubmit: (data: ReportFormData) => void }) => void} openModal
 * @property {() => void} closeModal
 */

export default forwardRef(
  /**
   * @param {Object} props
   * @param {React.ForwardedRef<ReportFormDialogComponent>} ref
   */
  function ReportFormDialog(props, ref) {
    const { useActiveChatSession, useActiveSessionHasActiveStream } = useChatContext()

    const [isModalOpen, setIsModalOpen] = useState(false)
    /** @type {ReactState<ChatReportInput["images"] | null>} */
    const [reportImages, setReportImages] = useState(null)
    /** @type {ReactState<((data: ReportFormData) => void) | null>} */
    const [onSubmit, setOnSubmit] = useState(null)
    const { data: activeChatSession } = useActiveChatSession()
    const isActiveSessionAwaitingResponse = useActiveSessionHasActiveStream()
    const isLoadingResponse = isActiveSessionAwaitingResponse
    const {savePatient} = useSavePatient()

    const formId = useId()
    const formDialogRef = useRef(null)

    /** @param {string} id */
    function getScopedId(id) { return `${formId}-${id}` }

    /** @type {ReportFormDialogComponent["openModal"]} */
    function openModal(images, { onSubmit }) {
      setIsModalOpen(true)
      setReportImages(images)
      setOnSubmit(() => onSubmit)
    }

    function closeModal() {
      setIsModalOpen(false)
      setReportImages(null)
      setOnSubmit(null)
    }

    useImperativeHandle(ref, () => ({
      isModalOpen,
      openModal,
      closeModal,
    }), [isModalOpen])

    useEffect(() => {
      if (!isModalOpen) return

      formDialogRef.current?.showModal()
      return () => formDialogRef.current?.close()
    }, [isModalOpen])

    /** @type {ReactState<{ id: string, firstName: string, lastName: string, age: number, gender: string } | null>} */
    const [initialData, setInitialData] = useState(null)
    useEffect(() => {
      if (isModalOpen) return

      let patientInfo = activeChatSession?.patientInfo
      if (patientInfo == null) {
        setInitialData(null)
        return
      }

      let { firstName, lastName } = splitName(patientInfo.name)

      setInitialData({
        id: patientInfo.id,
        firstName,
        lastName,
        age: patientInfo.age,
        gender: patientInfo.gender
      })
    }, [activeChatSession?.patientInfo, isModalOpen])

    const [modalityValue, setModalityValue] = useState("")
    /** @type {React.RefObject<HTMLInputElement | null>} */
    const modalitySelectInputRef = useRef(null)

    useEffect(() => {
      if (modalitySelectInputRef.current == null) return

      if (modalityValue == "") {
        modalitySelectInputRef.current.setCustomValidity("Select value from list of options");
      } else {
        modalitySelectInputRef.current.setCustomValidity("")
        mixpanel.track("Select Modality", {
          modality: modalityValue
        })
      }
    }, [modalityValue])

    /** @param {React.FormEvent<HTMLFormElement>} ev */
    function handleSubmit(ev) {
      ev.preventDefault()

      if (isLoadingResponse) return

      const formData = new FormData(ev.target)

      let data = {
        patientInfo: {
          name: [
            formData.get("patient-first-name"),
            formData.get("patient-last-name")
          ].join(" ").split(" ").map(capitalise).join(" "),
          id: formData.get("patient-id").toUpperCase(),
          age: Number(formData.get("patient-age")),
          gender: formData.get("patient-gender"),
        },
        images: reportImages,
        modality: modalityValue,
        technique: capitalise(formData.get("technique")),
        examinationReason: capitalise(formData.get("examination-reason")),
        clinicalHistory: capitalise(formData.get("clinical-history")),
        mode: formData.get("advanced-analysis") === "on" ? "advanced" : "default",
      }

      savePatient(data.patientInfo)
      mixpanel.track("Generate Report", {
        patientName: data.patientInfo.name,
        patientAge: data.patientInfo.age,
        patientGender: data.patientInfo.gender,
        modality: data.modality,
        technique: data.technique,
        mode: data.mode,
        hasExaminationReason: !!data.examinationReason,
        hasClinicalHistory: !!data.clinicalHistory
      })
      onSubmit?.(data)

      setTimeout(() => {
        closeModal()
        ev.target.reset?.()
      }, 0)
    }

    const firstNameInputRef = useRef(null)
    const lastNameInputRef = useRef(null)
    const patientIdInputRef = useRef(null)
    const genderFieldsetRef = useRef(null)
    const ageInputRef = useRef(null)

    /** @param {*} patient */
    function autoFillPatientInfo(patient) {
      if (patient == null) return

      let { firstName, lastName } = splitName(patient.patientName)

      if (firstNameInputRef.current != null) firstNameInputRef.current.value = firstName
      if (lastNameInputRef.current != null) lastNameInputRef.current.value = lastName
      if (patientIdInputRef.current != null) patientIdInputRef.current.value = patient.patientId
      if (genderFieldsetRef.current != null) {
        for (let radio of genderFieldsetRef.current.querySelectorAll("input[type=radio]")) {
          radio.checked = radio.value === patient.gender
        }
      }
      if (ageInputRef.current != null) ageInputRef.current.value = patient.age
    }

    return (
      <dialog
        className="report-form-dialog"
        onClose={() => closeModal()}
        ref={formDialogRef}
      >
        <div className="report-form-dialog__body">
          <header>
            <h2>Generate medical report</h2>
            <p>Add relevant data required to generate the medical report</p>

            <div className="report-form-dialog__close-btn-wrapper">
              <button
                type="button"
                className="report-form-dialog__close-btn"
                title="Close report panel"
                onClick={closeModal}
              >
                <IoMdClose />
                <span className="sr-only">Close report panel</span>
              </button>
            </div>
          </header>

          <form
            method="dialog"
            className="report-form-dialog__form"
            id={formId}
            onSubmit={handleSubmit}
            key={initialData}
          >
            <div className="report-form-dialog__form-fields">
              <fieldset className="report-form-dialog__form-fieldset">
                <legend className="report-form-dialog__form-fieldset-legend">Patient information</legend>

                <div className="report-form-dialog__form-inline-fieldgroup">
                  <div className="report-form-dialog__form-field">
                    <label
                      className="report-form-dialog__form-field-label"
                      htmlFor={getScopedId("patient-first-name")}
                    >First Name <span className="text-red-500">*</span></label>
                    <PatientAutoComplete
                      className="patient-autocomplete report-form-dialog__form-field-input-wrapper"
                      searchKey="patientName"
                      onSelectPatient={autoFillPatientInfo}
                      filterFn={(search, patient) => {
                        let { firstName = "" } = splitName(patient.patientName)
                        return isCaseInsensitiveSubstring(search, firstName)
                      }}
                    >
                      <PatientAutoComplete.Input
                        type="text"
                        className="report-form-dialog__form-field-input"
                        name="patient-first-name"
                        id={getScopedId("patient-first-name")}
                        defaultValue={initialData?.firstName}
                        required
                        ref={firstNameInputRef}
                      />
                      <PatientAutoComplete.List className="report-form-dialog__form-field-combobox-list" />
                    </PatientAutoComplete>
                  </div>
                  <div className="report-form-dialog__form-field">
                    <label
                      className="report-form-dialog__form-field-label"
                      htmlFor={getScopedId("patient-last-name")}
                    >Last Name <span className="text-red-500">*</span></label>
                    <PatientAutoComplete
                      className="patient-autocomplete report-form-dialog__form-field-input-wrapper"
                      searchKey="patientName"
                      onSelectPatient={autoFillPatientInfo}
                      filterFn={(search, patient) => {
                        let { lastName = "" } = splitName(patient.patientName)
                        return isCaseInsensitiveSubstring(search, lastName)
                      }}
                    >
                      <PatientAutoComplete.Input
                        type="text"
                        name="patient-last-name"
                        className="report-form-dialog__form-field-input"
                        id={getScopedId("patient-last-name")}
                        defaultValue={initialData?.lastName}
                        required
                        ref={lastNameInputRef}
                      />
                      <PatientAutoComplete.List className="report-form-dialog__form-field-combobox-list" />
                    </PatientAutoComplete>
                  </div>
                </div>

                <div className="report-form-dialog__form-inline-fieldgroup">
                  <div className="report-form-dialog__form-field">
                    <label
                      className="report-form-dialog__form-field-label"
                      htmlFor={getScopedId("patient-age")}
                    >Age <span className="text-red-500">*</span></label>
                    <input
                      type="number"
                      className="report-form-dialog__form-field-input"
                      name="patient-age"
                      id={getScopedId("patient-age")}
                      defaultValue={initialData?.age}
                      min={0}
                      required
                      ref={ageInputRef}
                    />
                  </div>
                  <div className="report-form-dialog__form-field">
                    <label
                      className="report-form-dialog__form-field-label"
                      htmlFor={getScopedId("patient-id")}
                    >Patient ID <span className="text-red-500">*</span></label>
                    <PatientAutoComplete
                      className="patient-autocomplete report-form-dialog__form-field-input-wrapper"
                      searchKey="patientId"
                      onSelectPatient={autoFillPatientInfo}
                    >
                      <PatientAutoComplete.Input
                        type="text"
                        className="report-form-dialog__form-field-input"
                        name="patient-id"
                        id={getScopedId("patient-id")}
                        defaultValue={initialData?.id}
                        required
                        ref={patientIdInputRef}
                      />
                      <PatientAutoComplete.List className="report-form-dialog__form-field-combobox-list" />
                    </PatientAutoComplete>
                  </div>
                </div>

                <fieldset className="report-form-dialog__form-field" ref={genderFieldsetRef}>
                  <legend className="report-form-dialog__form-field-label">Gender (Sex) <span className="text-red-500">*</span></legend>

                  <div className="report-form-dialog__form-radio-fieldgroup">
                    <div className="report-form-dialog__form-radio-field">
                      <input
                        type="radio"
                        className="report-form-dialog__form-field-input"
                        name="patient-gender"
                        id={getScopedId("patient-gender-male")}
                        value="Male"
                        defaultChecked={initialData?.gender === "Male"}
                        required
                      />
                      <label
                        className="report-form-dialog__form-radio-field-label"
                        htmlFor={getScopedId("patient-gender-male")}
                      >Male</label>
                    </div>
                    <div className="report-form-dialog__form-radio-field">
                      <input
                        type="radio"
                        className="report-form-dialog__form-field-input"
                        name="patient-gender"
                        id={getScopedId("patient-gender-female")}
                        value="Female"
                        defaultChecked={initialData?.gender === "Female"}
                        required
                      />
                      <label
                        className="report-form-dialog__form-radio-field-label"
                        htmlFor={getScopedId("patient-gender-female")}
                      >Female</label>
                    </div>
                  </div>
                </fieldset>
              </fieldset>

              <div className="report-form-dialog__form-field">
                <label
                  className="report-form-dialog__form-field-label"
                  htmlFor={getScopedId("modality")}
                >Modality <span className="text-red-500">*</span></label>
                <ModalitySelect
                  type="text"
                  id={getScopedId("modality")}
                  className="report-form-dialog__form-field-input"
                  defaultSelectedValue=""
                  onChange={ev => {
                    setModalityValue("")
                  }}
                  onSelectModality={modality => {
                    setModalityValue(modality.value)
                  }}
                  ref={modalitySelectInputRef}
                />
              </div>

              <div className="report-form-dialog__form-field">
                <label
                  className="report-form-dialog__form-field-label"
                  htmlFor={getScopedId("technique")}
                >Technique/View <span className="text-red-500">*</span></label>
                <AutoResizingTextarea
                  className="report-form-dialog__form-field-input !min-h-[2.75rem]"
                  data-variant="small"
                  name="technique"
                  id={getScopedId("technique")}
                  rows={1}
                  placeholder="Eg. Lateral/frontal, AP/PA"
                  required
                />
              </div>

              <fieldset className="report-form-dialog__form-fieldset">
                <legend className="report-form-dialog__form-fieldset-legend">Clinical Information</legend>

                <div className="report-form-dialog__form-field">
                  <label
                    className="report-form-dialog__form-field-label"
                    htmlFor={getScopedId("examination-reason")}
                  >Reason for examination <span className="text-red-500">*</span></label>
                  <AutoResizingTextarea
                    className="report-form-dialog__form-field-input"
                    name="examination-reason"
                    id={getScopedId("examination-reason")}
                    required
                  />
                </div>
                <div className="report-form-dialog__form-field">
                  <label
                    className="report-form-dialog__form-field-label"
                    htmlFor={getScopedId("clinical-history")}
                  >Relevant Clinical History</label>
                  <AutoResizingTextarea
                    className="report-form-dialog__form-field-input"
                    name="clinical-history"
                    id={getScopedId("clinical-history")}
                  />
                </div>
              </fieldset>

              <div className="report-form-dialog__form-field advanced-mode-field">
                <div className="advanced-mode-field__input-wrapper">
                  <input
                    type="checkbox"
                    className="report-form-dialog__form-field-input advanced-mode-field__input"
                    name="advanced-analysis"
                    id={getScopedId("advanced-analysis")}
                    onChange={(e) => {
                      mixpanel.track("Toggle Advanced Analysis", {
                        enabled: e.target.checked
                      })
                    }}
                  />
                  <label
                    className="advanced-mode-field__label"
                    htmlFor={getScopedId("advanced-analysis")}
                  >Advanced analysis</label>

                  <div className="advanced-mode-field__badge">Additional cost</div>
                </div>

                <div>
                  <p className="advanced-mode-field__description">
                    Enabling this option will allow us to provide more detailed analysis of the image.
                  </p>
                </div>
              </div>
            </div>

            <div className="report-form-dialog__btn-group">
              <button
                type="submit"
                className="report-form-dialog__btn"
                data-variant="primary"
                disabled={isLoadingResponse}
              >Continue</button>
            </div>
          </form>
        </div>
      </dialog>
    )
  }
)

/**
 * @typedef {Object} ReportFormData
 * @property {Object} patientInfo
 * @property {string} patientInfo.name
 * @property {string} patientInfo.id
 * @property {number} patientInfo.age
 * @property {string} patientInfo.gender
 * @property {string} image
 * @property {string} modality
 * @property {string} technique
 * @property {string} examinationReason
 * @property {string} clinicalHistory
 */

/**
 * @typedef {Object} ModalitySelectExtraProps
 * @property {string} [defaultSelectedValue]
 * @property {(modality: typeof MODALITIES[number]) => void} [onSelectModality]
 * @property {React.ComponentProps<Combobox>} [wrapperProps]
 */

export const ModalitySelect = forwardRef(
  /**
   * @param {ModalitySelectExtraProps & React.ComponentProps<Combobox.Input>} props 
   * @param {React.Ref<HTMLInputElement>} ref
   */
  function ModalitySelect({
    defaultSelectedValue = "",
    onSelectModality,
    wrapperProps: { className: wrapperClassName, ...restOfWrapperProps } = {},
    className,
    onChange,
    onFocus,
    ...restOfProps
  }, ref) {
    const [selected, setSelected] = useState(
      MODALITIES.find(modality => modality.value === defaultSelectedValue)
    )
    const [filterValue, setFilterValue] = useState(
      MODALITIES.find(modality => modality.value === defaultSelectedValue)?.label ?? ""
    )

    const filteredModalities = useMemo(() => (
      MODALITIES
        .filter(modality => (
          modality.label.toLowerCase().includes(filterValue.trim().toLowerCase()) ||
          modality.value === "other"
        ))
        .sort((a, b) => {
          if (a.value === "other") return 1
          if (b.value === "other") return -1

          let startIndexA = a.label.toLocaleLowerCase().indexOf(filterValue.trim().toLowerCase())
          if (startIndexA === -1) startIndexA = Infinity
          let startIndexB = b.label.toLocaleLowerCase().indexOf(filterValue.trim().toLowerCase())
          if (startIndexB === -1) startIndexB = Infinity

          let diff = startIndexA - startIndexB
          if (diff < 0) return -1
          if (diff > 0) return 1
          return 0
        })
    ), [filterValue])

    const randomId = useMemo(() => filteredModalities.reduce((acc, modality) => (
      acc += MODALITIES.findIndex(item => item.value === modality.value)
    ), 0), [filteredModalities])

    return (
      <Combobox
        className={`modality-select report-form-dialog__form-field-input-wrapper ${wrapperClassName ?? ""}`}
        defaultSelectedOption={selected?.value}
        onSelectOption={value => {
          const modality = filteredModalities.find(modality => (
            modality.value.toLowerCase() === value.toLowerCase()
          ))
          setSelected(modality)
          onSelectModality?.(modality)
          setFilterValue(modality?.label ?? "")
        }}
        {...restOfWrapperProps}
      >
        <Combobox.WithContext>
          {({ setIsListExpanded }) => (
            <Combobox.Input
              className={`report-form-dialog__form-field-input ${className ?? ""}`}
              value={filterValue}
              required
              onChange={ev => {
                setFilterValue(ev.target.value)
                onChange?.(ev)
              }}
              onFocus={ev => {
                setIsListExpanded(true)
                onFocus?.(ev)
              }}
              ref={ref}
              {...restOfProps}
            />
          )}
        </Combobox.WithContext>
        <Combobox.Listbox className="report-form-dialog__form-field-combobox-list">
          {filteredModalities.map(modality => (
            modality.value === "" ? null :
              <Combobox.Option
                className="report-form-dialog__form-field-combobox-option"
                value={modality.value}
                key={modality.value + randomId}
              >{modality.label}</Combobox.Option>
          ))}
        </Combobox.Listbox>
      </Combobox>
    )
  }
)

/** @param {string} modality */
export function getModalityLabel(modality) {
  let item = MODALITIES.find(item => item.value === modality)
  return item?.label ?? "Select modality"
}

const MODALITIES = [
  { label: 'Abdominal Ultrasound', value: 'abdominal ultrasound' },
  { label: 'Angiography', value: 'angiography' },
  { label: 'Barium Swallow', value: 'barium swallow' },
  { label: 'Breast Ultrasound', value: 'breast ultrasound' },
  { label: 'Bronchoscopy', value: 'bronchoscopy' },
  { label: 'Chest X-ray', value: 'chest x-ray' },
  { label: 'Colonoscopy', value: 'colonoscopy' },
  { label: 'CT', value: 'ct' },
  { label: 'Dermatology (SOAP)', value: 'dermatology' },
  { label: 'Echocardiography', value: 'echocardiography' },
  { label: 'Electrocardiogram', value: 'electrocardiogram' },
  { label: 'Gastroscopy', value: 'gastroscopy' },
  { label: 'Hysterosalpingogram', value: 'hysterosalpingogram' },
  { label: 'Intravenous Urogram', value: 'intravenous urogram' },
  { label: 'Limb X-ray', value: 'limb x-ray' },
  { label: 'Malaria Microscopy', value: 'malaria microscopy' },
  { label: 'Mammogram', value: 'mammogram' },
  { label: 'MRI', value: 'mri' },
  { label: 'Myelogram', value: 'myelogram' },
  { label: 'Ophthalmoscopy', value: 'ophthalmoscopy' },
  { label: 'Other', value: 'others' },
  { label: 'Clinical Summary', value: 'clinical_summary' },
  { label: 'Laboratory Interpretation', value: 'laboratory' },
  { label: 'Pathology', value: 'pathology' },
  { label: 'Retrograde Urethrogram', value: 'retrograde urethrogram' },
  { label: 'Sialogram', value: 'sialogram' },
  { label: 'Spine X-ray', value: 'spine x-ray' },
  { label: 'Ultrasound', value: 'ultrasound' },
  { label: 'Voiding Cystourethrogram', value: 'voiding cystourethrogram' },
  { label: 'X-ray', value: 'x-ray' }
].sort(((a, b) => {
  // Arrange in alphabetical order and put "other" value at the bottom
  if (b.value === 'others') return -1;
  if (a.value === 'others') return 1;
  return a.label.localeCompare(b.label);
}))

/** @param {string} name */
function splitName(name) {
  const parts = name.split(" ")
  if (parts.length === 1) return { firstName: parts[0], lastName: "" }
  return {
    firstName: parts.slice(0, -1).join(" "),
    lastName: parts[parts.length - 1],
  }
}

/**
 * @param {string} search
 * @param {string} value
 */
function isCaseInsensitiveSubstring(search = "", value = "") {
  return value.toLowerCase().includes(search.toLowerCase())
}
